import { useState, useContext, useEffect, useCallback } from "react";
import '../../styles/page-titles.css'
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import { SidebarContext } from "../../AppRoutes";
import { getRelatorioGeografico } from "../../services/api";
import { nowTz } from '../../utils/timezone';
import Loading from "react-loading";
import { FaMapMarkerAlt, FaTruck, FaStore, FaMoneyBillWave, FaChartBar } from 'react-icons/fa';
import { MdDeliveryDining, MdStorefront, MdLocationCity, MdTableRestaurant } from 'react-icons/md';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatPriceWithSymbol } from '../../utils/currency';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    .filters {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    input[type="date"] {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
    }

    button {
        padding: 8px 16px;
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.9;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    @media (max-width: 600px) {
        h1 {
            font-size: 18px;
        }

        .filters {
            width: 100%;
        }

        input[type="date"], button {
            flex: 1;
        }
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
`;

const MetricCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;

    .icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        margin-bottom: 8px;
    }

    .icon.blue { background: rgba(49, 140, 213, 0.1); color: rgb(49, 140, 213); }
    .icon.orange { background: rgba(255, 152, 0, 0.1); color: #ff9800; }
    .icon.green { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .icon.purple { background: rgba(102, 126, 234, 0.1); color: #667eea; }

    .label {
        font-size: 14px;
        color: #666;
    }

    .value {
        font-size: 28px;
        font-weight: 700;
        color: #333;
    }

    .sub {
        font-size: 12px;
        color: #999;
    }

    @media (max-width: 500px) {
        padding: 14px;
        .value { font-size: 22px; }
    }
`;

const FullWidthChart = styled.div`
    background: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    margin-bottom: 30px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .chart-container {
        height: 400px;
        width: 100%;
    }

    @media (max-width: 600px) {
        padding: 16px;

        .chart-container {
            height: 300px;
        }
    }
`;

const BairrosTable = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    margin-bottom: 20px;

    .table-header {
        padding: 20px 24px;
        border-bottom: 1px solid #e2e8f0;

        h3 {
            font-size: 18px;
            font-weight: 600;
            color: #2d3748;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }

    .table-wrapper {
        overflow-x: auto;
    }

    table {
        width: 100%;
        border-collapse: collapse;

        thead {
            background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));

            th {
                padding: 16px;
                text-align: left;
                font-weight: 600;
                font-size: 13px;
                color: white;
                white-space: nowrap;

                &:first-child {
                    padding-left: 24px;
                }
            }
        }

        tbody {
            tr {
                border-bottom: 1px solid #f1f5f9;
                transition: background-color 0.2s ease;

                &:hover {
                    background: #f8fafc;
                }

                td {
                    padding: 14px 16px;
                    color: #2d3748;
                    font-size: 14px;

                    &:first-child {
                        padding-left: 24px;
                    }

                    .bairro-name {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        font-weight: 500;
                    }

                    .bar-container {
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        .bar {
                            height: 8px;
                            border-radius: 4px;
                            background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
                            transition: width 0.3s ease;
                        }

                        .percentage {
                            font-size: 12px;
                            color: #666;
                            min-width: 40px;
                        }
                    }

                    .valor {
                        font-weight: 600;
                        color: rgb(49, 140, 213);
                    }

                    .taxa {
                        font-size: 13px;
                        color: #ff9800;
                        font-weight: 500;
                    }
                }
            }
        }
    }

    @media (max-width: 768px) {
        table {
            min-width: 600px;
        }
    }
`;

const ModalidadeComparison = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr 1fr;
    }

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const ModalidadeCard = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    .icon-container {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 36px;
        margin-bottom: 16px;

        &.entrega {
            background: linear-gradient(135deg, rgba(49, 140, 213, 0.1), rgba(79, 172, 254, 0.2));
            color: rgb(49, 140, 213);
        }

        &.retirada {
            background: linear-gradient(135deg, rgba(40, 167, 69, 0.1), rgba(76, 217, 100, 0.2));
            color: #28a745;
        }

        &.mesa {
            background: linear-gradient(135deg, rgba(255, 152, 0, 0.1), rgba(255, 193, 7, 0.2));
            color: #ff9800;
        }
    }

    .title {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin-bottom: 8px;
    }

    .stats {
        display: flex;
        gap: 24px;
        margin-top: 16px;

        .stat {
            .value {
                font-size: 24px;
                font-weight: 700;
                color: #333;
            }

            .label {
                font-size: 12px;
                color: #666;
            }
        }
    }

    .percentage-bar {
        width: 100%;
        height: 8px;
        background: #e9ecef;
        border-radius: 4px;
        margin-top: 16px;
        overflow: hidden;

        .fill {
            height: 100%;
            border-radius: 4px;
            transition: width 0.5s ease;

            &.entrega {
                background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
            }

            &.retirada {
                background: linear-gradient(135deg, #28a745, #4cd964);
            }

            &.mesa {
                background: linear-gradient(135deg, #ff9800, #ffc107);
            }
        }
    }

    .percentage-text {
        margin-top: 8px;
        font-size: 14px;
        font-weight: 600;
        color: #666;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
`;

const COLORS = ['#318CD5', '#4FACFE', '#7CC4FF', '#28a745', '#ff9800', '#dc3545', '#6f42c1', '#20c997'];

const RelatorioGeografico = () => {
    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);

    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('reports');

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState({
        resumo: {
            total_pedidos: 0,
            total_delivery: 0,
            total_retirada: 0,
            total_mesa: 0,
            percentual_delivery: 0,
            percentual_retirada: 0,
            percentual_mesa: 0,
            faturamento_delivery: 0,
            faturamento_retirada: 0,
            faturamento_mesa: 0
        },
        todos_bairros: [],
        top_bairros: []
    });
    const [startDate, setStartDate] = useState(nowTz().subtract(30, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(nowTz().format('YYYY-MM-DD'));

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getRelatorioGeografico(startDate, endDate);
            if (response?.data?.success) {
                setData(response.data);
            }
        } catch (error) {
            console.error('[RELATORIO-GEOGRAFICO] Erro ao buscar dados:', error);
            toast.error(t('common.fetchError'));
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Preparar dados para gráfico de barras (bairros)
    const bairrosBarData = (data.top_bairros || []).slice(0, 10).map(b => ({
        name: b.bairro || t('geographic.notInformed'),
        pedidos: b.pedidos || 0,
        faturamento: b.faturamento || 0
    }));

    // Calcular total para porcentagens
    const totalPedidos = data.resumo?.total_pedidos || 1;
    const maxBairroPedidos = Math.max(...(data.todos_bairros || []).map(b => b.pedidos || 0), 1);

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const entry = payload[0].payload;
            return (
                <div style={{
                    background: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #e0e0e0'
                }}>
                    <p style={{ margin: 0, fontWeight: 600, color: '#333' }}>{entry.name}</p>
                    <p style={{ margin: '4px 0 0', color: '#318CD5', fontSize: '13px' }}>
                        {t('common.orders')}: {entry.pedidos || entry.size}
                    </p>
                    {entry.faturamento !== undefined && (
                        <p style={{ margin: '4px 0 0', color: '#28a745', fontSize: '13px' }}>
                            {t('common.revenue')}: {formatPriceWithSymbol(entry.faturamento)}
                        </p>
                    )}
                </div>
            );
        }
        return null;
    };

    return (
        <>
            <PermissionGate permissions={['default']}>
                <Container sidebar={sidebar}>
                    <Header>
                        <div className="page-title-with-icon">
                            <div className="page-icon"><FaMapMarkerAlt /></div>
                            <h1 className="page-title">{t('geographic.title')}</h1>
                        </div>
                        <div className="filters" style={{ marginRight: '50px' }}>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            <button onClick={fetchData} disabled={isLoading}>
                                {isLoading ? t('common.loading') : t('common.update')}
                            </button>
                        </div>
                    </Header>

                    {isLoading ? (
                        <LoadingContainer>
                            <Loading type="spin" color="rgb(49, 140, 213)" height={40} width={40} />
                        </LoadingContainer>
                    ) : (
                        <>
                            <CardsGrid>
                                <MetricCard>
                                    <div className="icon blue">
                                        <FaChartBar />
                                    </div>
                                    <span className="label">{t('geographic.totalOrders')}</span>
                                    <span className="value">{data.resumo?.total_pedidos || 0}</span>
                                    <span className="sub">{t('common.inPeriod')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon orange">
                                        <MdDeliveryDining />
                                    </div>
                                    <span className="label">{t('geographic.deliveries')}</span>
                                    <span className="value">{data.resumo?.total_delivery || 0}</span>
                                    <span className="sub">{t('common.ofTotal', { value: parseFloat(data.resumo?.percentual_delivery || 0).toFixed(1) })}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon green">
                                        <MdStorefront />
                                    </div>
                                    <span className="label">{t('geographic.pickups')}</span>
                                    <span className="value">{data.resumo?.total_retirada || 0}</span>
                                    <span className="sub">{t('common.ofTotal', { value: parseFloat(data.resumo?.percentual_retirada || 0).toFixed(1) })}</span>
                                </MetricCard>

                                {(data.resumo?.total_mesa > 0) && (
                                    <MetricCard>
                                        <div className="icon orange">
                                            <MdTableRestaurant />
                                        </div>
                                        <span className="label">{t('geographic.mesaOrders')}</span>
                                        <span className="value">{data.resumo?.total_mesa || 0}</span>
                                        <span className="sub">{t('common.ofTotal', { value: parseFloat(data.resumo?.percentual_mesa || 0).toFixed(1) })}</span>
                                    </MetricCard>
                                )}

                                <MetricCard>
                                    <div className="icon purple">
                                        <FaMoneyBillWave />
                                    </div>
                                    <span className="label">{t('geographic.deliveryRevenue')}</span>
                                    <span className="value">
                                        {formatPriceWithSymbol(data.resumo?.faturamento_delivery || 0)}
                                    </span>
                                    <span className="sub">{t('common.collectedDeliveries')}</span>
                                </MetricCard>

                                {(data.resumo?.faturamento_mesa > 0) && (
                                    <MetricCard>
                                        <div className="icon orange">
                                            <FaMoneyBillWave />
                                        </div>
                                        <span className="label">{t('geographic.mesaRevenue')}</span>
                                        <span className="value">
                                            {formatPriceWithSymbol(data.resumo?.faturamento_mesa || 0)}
                                        </span>
                                        <span className="sub">{t('geographic.collectedMesa')}</span>
                                    </MetricCard>
                                )}
                            </CardsGrid>

                            <ModalidadeComparison>
                                <ModalidadeCard>
                                    <div className="icon-container entrega">
                                        <FaTruck />
                                    </div>
                                    <span className="title">{t('geographic.deliveryCard')}</span>
                                    <div className="stats">
                                        <div className="stat">
                                            <div className="value">{data.resumo?.total_delivery || 0}</div>
                                            <div className="label">{t('common.orders')}</div>
                                        </div>
                                        <div className="stat">
                                            <div className="value">
                                                {formatPriceWithSymbol((data.resumo?.faturamento_delivery || 0) / Math.max(data.resumo?.total_delivery || 1, 1))}
                                            </div>
                                            <div className="label">{t('common.avgTicket')}</div>
                                        </div>
                                    </div>
                                    <div className="percentage-bar">
                                        <div
                                            className="fill entrega"
                                            style={{ width: `${parseFloat(data.resumo?.percentual_delivery || 0)}%` }}
                                        />
                                    </div>
                                    <span className="percentage-text">
                                        {t('geographic.ordersOfTotal', { value: parseFloat(data.resumo?.percentual_delivery || 0).toFixed(1) })}
                                    </span>
                                </ModalidadeCard>

                                <ModalidadeCard>
                                    <div className="icon-container retirada">
                                        <FaStore />
                                    </div>
                                    <span className="title">{t('geographic.pickupCard')}</span>
                                    <div className="stats">
                                        <div className="stat">
                                            <div className="value">{data.resumo?.total_retirada || 0}</div>
                                            <div className="label">{t('common.orders')}</div>
                                        </div>
                                        <div className="stat">
                                            <div className="value">
                                                {formatPriceWithSymbol((data.resumo?.faturamento_retirada || 0) / Math.max(data.resumo?.total_retirada || 1, 1))}
                                            </div>
                                            <div className="label">{t('common.avgTicket')}</div>
                                        </div>
                                    </div>
                                    <div className="percentage-bar">
                                        <div
                                            className="fill retirada"
                                            style={{ width: `${parseFloat(data.resumo?.percentual_retirada || 0)}%` }}
                                        />
                                    </div>
                                    <span className="percentage-text">
                                        {t('geographic.ordersOfTotal', { value: parseFloat(data.resumo?.percentual_retirada || 0).toFixed(1) })}
                                    </span>
                                </ModalidadeCard>

                                <ModalidadeCard>
                                    <div className="icon-container mesa">
                                        <MdTableRestaurant />
                                    </div>
                                    <span className="title">{t('geographic.mesaCard')}</span>
                                    <div className="stats">
                                        <div className="stat">
                                            <div className="value">{data.resumo?.total_mesa || 0}</div>
                                            <div className="label">{t('common.orders')}</div>
                                        </div>
                                        <div className="stat">
                                            <div className="value">
                                                {formatPriceWithSymbol((data.resumo?.faturamento_mesa || 0) / Math.max(data.resumo?.total_mesa || 1, 1))}
                                            </div>
                                            <div className="label">{t('common.avgTicket')}</div>
                                        </div>
                                    </div>
                                    <div className="percentage-bar">
                                        <div
                                            className="fill mesa"
                                            style={{ width: `${parseFloat(data.resumo?.percentual_mesa || 0)}%` }}
                                        />
                                    </div>
                                    <span className="percentage-text">
                                        {t('geographic.ordersOfTotal', { value: parseFloat(data.resumo?.percentual_mesa || 0).toFixed(1) })}
                                    </span>
                                </ModalidadeCard>
                            </ModalidadeComparison>

                            <FullWidthChart>
                                <h3>
                                    <MdLocationCity />
                                    {t('geographic.top10Title')}
                                </h3>
                                <div className="chart-container">
                                    {bairrosBarData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={bairrosBarData} layout="vertical">
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                <XAxis type="number" />
                                                <YAxis
                                                    type="category"
                                                    dataKey="name"
                                                    width={100}
                                                    tick={{ fontSize: 11 }}
                                                />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Bar dataKey="pedidos" fill="#318CD5" radius={[0, 8, 8, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                                            {t('geographic.noBairroData')}
                                        </div>
                                    )}
                                </div>
                            </FullWidthChart>

                            <BairrosTable>
                                <div className="table-header">
                                    <h3>
                                        <FaMapMarkerAlt color="rgb(49, 140, 213)" />
                                        {t('geographic.detailTitle')}
                                    </h3>
                                </div>
                                <div className="table-wrapper">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>{t('geographic.tableHeaders.neighborhood')}</th>
                                                <th>{t('geographic.tableHeaders.orders')}</th>
                                                <th>{t('geographic.tableHeaders.participation')}</th>
                                                <th>{t('geographic.tableHeaders.revenue')}</th>
                                                <th>{t('geographic.tableHeaders.avgFee')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(data.todos_bairros || []).map((bairro, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="bairro-name">
                                                            <FaMapMarkerAlt color={COLORS[index % COLORS.length]} />
                                                            {bairro.bairro || t('geographic.notInformed')}
                                                        </div>
                                                    </td>
                                                    <td>{bairro.pedidos || 0}</td>
                                                    <td>
                                                        <div className="bar-container">
                                                            <div
                                                                className="bar"
                                                                style={{
                                                                    width: `${((bairro.pedidos || 0) / maxBairroPedidos) * 100}px`
                                                                }}
                                                            />
                                                            <span className="percentage">
                                                                {(((bairro.pedidos || 0) / totalPedidos) * 100).toFixed(1)}%
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="valor">
                                                        {formatPriceWithSymbol(bairro.faturamento || 0)}
                                                    </td>
                                                    <td className="taxa">
                                                        {formatPriceWithSymbol(bairro.taxa_media || 0)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </BairrosTable>
                        </>
                    )}
                </Container>
            </PermissionGate>
        </>
    );
};

export default RelatorioGeografico;
